import React, { useState, ButtonHTMLAttributes, ReactNode, CSSProperties } from 'react';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'tinted' | 'plain' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
  full?: boolean;
  loading?: boolean;
  style?: CSSProperties;
};

type SizeSpec = { h: string; px: number; font: number; rad: string; gap: number };
type VariantSpec = { bg: string; fg: string; border: string; hover: string; press: string };

const SIZE_MAP: Record<ButtonSize, SizeSpec> = {
  sm: { h: 'var(--field-h-sm)', px: 12, font: 14, rad: 'var(--r-sm)', gap: 6 },
  md: { h: 'var(--field-h)', px: 16, font: 15, rad: 'var(--r-md)', gap: 8 },
  lg: { h: 'var(--field-h-lg)', px: 22, font: 17, rad: 'var(--r-md)', gap: 10 },
};

const VARIANT_MAP: Record<ButtonVariant, VariantSpec> = {
  primary: {
    bg: 'var(--accent)', fg: 'var(--fg-on-accent)', border: 'transparent',
    hover: 'var(--accent-hover)', press: 'var(--accent-press)',
  },
  secondary: {
    bg: 'var(--bg-surface)', fg: 'var(--fg-primary)', border: 'var(--line-border)',
    hover: 'var(--bg-fill-1)', press: 'var(--bg-fill-2)',
  },
  tinted: {
    bg: 'var(--accent-fill)', fg: 'var(--accent)', border: 'transparent',
    hover: 'var(--accent-fill-strong)', press: 'var(--accent-fill-strong)',
  },
  plain: {
    bg: 'transparent', fg: 'var(--accent)', border: 'transparent',
    hover: 'var(--bg-fill-1)', press: 'var(--bg-fill-2)',
  },
  ghost: {
    bg: 'transparent', fg: 'var(--fg-primary)', border: 'transparent',
    hover: 'var(--bg-fill-1)', press: 'var(--bg-fill-2)',
  },
  danger: {
    bg: 'var(--danger)', fg: '#fff', border: 'transparent',
    hover: 'color-mix(in oklab, var(--danger) 88%, black)',
    press: 'color-mix(in oklab, var(--danger) 78%, black)',
  },
};

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconRight,
  children,
  full,
  loading,
  disabled,
  style,
  className = '',
  ...rest
}: ButtonProps) {
  const sizes = SIZE_MAP[size];
  const variants = VARIANT_MAP[variant];

  const [hover, setHover] = useState(false);
  const [press, setPress] = useState(false);

  return (
    <button
      disabled={disabled || loading}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPress(false); }}
      onMouseDown={() => setPress(true)}
      onMouseUp={() => setPress(false)}
      className={className}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: sizes.gap,
        height: sizes.h, padding: `0 ${sizes.px}px`,
        borderRadius: sizes.rad,
        fontSize: sizes.font,
        fontWeight: variant === 'primary' || variant === 'danger' ? 600 : 500,
        letterSpacing: '-0.2px',
        color: variants.fg,
        background: press ? variants.press : hover ? variants.hover : variants.bg,
        border: `1px solid ${variants.border}`,
        boxShadow: variant === 'primary' ? '0 1px 2px rgba(10,132,255,0.18)' : 'none',
        transform: press ? 'scale(0.98)' : 'scale(1)',
        transition: 'background var(--dur-fast) var(--ease-out), transform var(--dur-fast) var(--ease-out), opacity var(--dur-base) var(--ease-out)',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: full ? '100%' : 'auto',
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...rest}
    >
      {loading ? <Spinner size={size === 'sm' ? 14 : 16} /> : icon}
      {children}
      {iconRight}
    </button>
  );
}

export default Button;
